import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';

import { RootState } from '../..';
import { IUser } from '../../UserSlice';
import {
  adminCreateInvoiceAdminNote,
  adminCreateStripeInvoice,
  adminDeleteInvoiceAdminNote,
  adminGetInvoices,
  adminSendInvoiceMail,
  adminUpdateInvoice,
} from './adminApi';
import { createStripeInvoiceSession, getInvoiceById } from './api';
import { ICourse } from '../CoursesSlice';

export const INVOICE_STATUSES = {
  PENDING: 'PENDING',
  PAID: 'PAID',
  VOID: 'VOID',
} as const;

export const INVOICE_PAYMENT_METHODS = {
  STRIPE: 'STRIPE',
} as const;

export type TInvoiceStatus = typeof INVOICE_STATUSES[keyof typeof INVOICE_STATUSES];
export type TInvoicePaymentMethod = typeof INVOICE_PAYMENT_METHODS[keyof typeof INVOICE_PAYMENT_METHODS];

export interface IInvoiceCustomAmount {
  description: string;
  quantity: number;
  rate: number;
}

export interface IInvoiceAdminNote {
  _id: string;
  description: string;
  createdBy: string | IUser;
  createdAt: string;
}

export interface IInvoice {
  _id: string;
  user: string | IUser;
  currency: string;
  isCreditChecked: boolean;
  isCourseCreditChecked: boolean;
  creditQuantity?: number;
  creditRate?: number;
  creditDescription?: string;
  isCustomChecked: boolean;
  customAmounts?: IInvoiceCustomAmount[];
  paymentMethod: TInvoicePaymentMethod;
  createdBy: string | IUser;
  expiryDate: string;
  invoiceNumber: number;
  status: TInvoiceStatus;
  isWebhookCalled: boolean;
  stripeSessionId?: string;
  createdAt: string;
  adminNotes?: IInvoiceAdminNote[];
  updatedBy?: string | IUser;
  courseCreditQuantity?: number;
  courseRateAmount?: number;
  course?: string | ICourse;
}

export interface IInitialInvoicesState {
  status: 'idle' | 'loading' | 'rejected';
  invoiceData?: IInvoice;
  // Admin
  adminInvoiceListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IInvoice>;
  };
}

const initialState: IInitialInvoicesState = {
  status: 'idle',
  invoiceData: undefined,
  // Admin
  adminInvoiceListData: undefined,
};

export const InvoicesSlice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    resetInvoiceData: (state) => {
      state.invoiceData = undefined;
    },
    // Admin
    resetAdminInvoiceListData: (state) => {
      state.adminInvoiceListData = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getInvoiceById.fulfilled, (state, action) => {
        const result = action.payload;

        state.invoiceData = result;
      })
      .addCase(createStripeInvoiceSession.fulfilled, (state, action) => {
        const result = action.payload;

        if (result && result.stripeSession && result.stripeSession.url) {
          window.location.href = result.stripeSession.url;
        }
      });

    // Admin
    builder
      .addCase(adminGetInvoices.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminInvoiceListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminInvoiceListData = newAdminInvoiceListData;
      })
      .addCase(adminCreateStripeInvoice.fulfilled, (state, action) => {
        const result = action.payload;

        if (state.adminInvoiceListData) {
          const newAdminInvoiceListData = {
            ...state.adminInvoiceListData,
          };

          newAdminInvoiceListData.list.unshift(result);

          if (newAdminInvoiceListData.totalDocs) {
            newAdminInvoiceListData.totalDocs += 1;
          } else {
            newAdminInvoiceListData.totalDocs = 1;
          }

          state.adminInvoiceListData = newAdminInvoiceListData;
        }
      })
      .addCase(adminUpdateInvoice.fulfilled, (state, action) => {
        const result = action.payload;

        if (state.adminInvoiceListData) {
          const newAdminInvoiceListData = { ...state.adminInvoiceListData };

          const index = newAdminInvoiceListData.list.findIndex(
            (item) => item._id === result._id
          );

          if (index > -1) {
            newAdminInvoiceListData.list[index] = result;
          }

          state.adminInvoiceListData = newAdminInvoiceListData;
        }
      })
      .addCase(adminCreateInvoiceAdminNote.fulfilled, (state, action) => {
        const result = action.payload;

        if (state.adminInvoiceListData) {
          const newAdminInvoiceListData = { ...state.adminInvoiceListData };

          const index = newAdminInvoiceListData.list.findIndex(
            (item) => item._id === result._id
          );

          if (index > -1) {
            newAdminInvoiceListData.list[index] = result;
          }

          state.adminInvoiceListData = newAdminInvoiceListData;
        }
      })
      .addCase(adminDeleteInvoiceAdminNote.fulfilled, (state, action) => {
        const result = action.payload;

        if (state.adminInvoiceListData) {
          const newAdminInvoiceListData = { ...state.adminInvoiceListData };

          const index = newAdminInvoiceListData.list.findIndex(
            (item) => item._id === result._id
          );

          if (index > -1) {
            newAdminInvoiceListData.list[index] = result;
          }

          state.adminInvoiceListData = newAdminInvoiceListData;
        }
      });

    // Common Matchers
    builder
      .addMatcher(
        isPending(
          getInvoiceById,
          createStripeInvoiceSession,
          // Admin
          adminCreateStripeInvoice,
          adminGetInvoices,
          adminUpdateInvoice,
          adminSendInvoiceMail,
          adminCreateInvoiceAdminNote,
          adminDeleteInvoiceAdminNote
        ),
        (state) => {
          state.status = 'loading';
        }
      )
      .addMatcher(
        isRejected(
          getInvoiceById,
          createStripeInvoiceSession,
          // Admin
          adminCreateStripeInvoice,
          adminGetInvoices,
          adminUpdateInvoice,
          adminSendInvoiceMail,
          adminCreateInvoiceAdminNote,
          adminDeleteInvoiceAdminNote
        ),
        (state) => {
          state.status = 'rejected';
        }
      )
      .addMatcher(
        isFulfilled(
          getInvoiceById,
          createStripeInvoiceSession,
          // Admin
          adminCreateStripeInvoice,
          adminGetInvoices,
          adminUpdateInvoice,
          adminSendInvoiceMail,
          adminCreateInvoiceAdminNote,
          adminDeleteInvoiceAdminNote
        ),
        (state) => {
          state.status = 'idle';
        }
      );
  },
});

export const getInvoicesState = (state: RootState): RootState['invoices'] =>
  state.invoices;

export const {
  resetInvoiceData,
  // Admin
  resetAdminInvoiceListData,
} = InvoicesSlice.actions;

export * from './adminApi';
export default InvoicesSlice.reducer;
